import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { APIKey, FormSectionElements, LOVValues, ProgramContainer } from '../../entities/formTemplateDataType';
import FormValidationUtils from "../../utilities/FormValidationUtils";
import { getRootComponent } from '../../utilities/ElementUtils';
import axios from 'axios';
import SessionStorageService from '../../services/SessionStorageService';
import { formatDateCustom, formatDateWithCustomFormat } from '../../utilities/DateUtils';
import { ErrorAlertComponent } from '../odsCalloutBox/ErrorAlertComponent';
import { setBaseHeaders } from '../../utilities/submitUtils';
import './PagedSummaryListComponent.css';
import Processing from '../../commonComponent/ProcessingComponent';
import DataUtils from '../../utilities/DataUtils';
import CustomPopup from './PopupComponent';
import ReCaptchaButton from '../captcha/captcha-button';
import { resolveApiUrl } from '../../utilities/URLUtils';

export type PagedTableProps = {
    isEng: boolean;
    field: FormSectionElements;
    callbackStepIndex?: (index: number, returnIndex?: number) => void;
    callback?: (programContainer: ProgramContainer) => void;
};

const DEFAULT_PAGE_SIZE = 10;

function applyNameSentenceCase(item: any): void {
    if (!item) return;
    const toSentenceCase = (s: string | null | undefined): string => {
        if (s == null || typeof s !== 'string') return s ?? '';
        const t = s.trim();
        return t ? t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';
    };
    if (typeof item.fullName === 'string' && item.fullName.trim()) item.fullName = toSentenceCase(item.fullName);
    if (typeof item.name === 'string' && item.name.trim()) item.name = toSentenceCase(item.name);
}

// Module-level request cache to prevent duplicate requests across component re-mounts
// Only blocks truly simultaneous requests (within 100ms)
const requestCache = new Map<string, { timestamp: number; promise: Promise<any> | null }>();
const CACHE_DURATION = 100; // 100ms - only prevents truly simultaneous duplicate calls


const PagedSummaryListComponent = ({ field, isEng, callbackStepIndex, callback }: PagedTableProps) => {
    const [allData, setAllData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [errorVisible, setErrorVisible] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [filterTrigger, setFilterTrigger] = useState<number>(0);
    const [hasMounted, setHasMounted] = useState<boolean>(false);
    const [isClickLoading, setIsClickLoading] = useState<boolean>(false);
    const [isLastPage, setIsLastPage] = useState<boolean>(false);
    const [showPopup, setShowPopup] = useState<boolean>(false);
    const [popupColumn, setPopupColumn] = useState<FormSectionElements | null>(null);
    const [popupRecord, setPopupRecord] = useState<any>(null);

    const pageSize = field.PageSize || DEFAULT_PAGE_SIZE;
    const isClientSide = field.ClientSidePagination === true;
    const isLazyLoading = field.LazyLoading === true;
    const elements = field.subSectionList?.[0]?.ListOfFormSectionElements || [];

    // Track last fetched URL to prevent duplicate requests
    const lastFetchedUrlRef = useRef<string | null>(null);
    const isFetchingRef = useRef<boolean>(false);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const getElementValue = (fieldId: string, programContainer: any): string => {
        const sections = programContainer?.ListOfFormSections ?? [];

        const searchElements = (elements: FormSectionElements[]): string | null => {
            for (const element of elements ?? []) {
                if (element.Id === fieldId) {
                    return element.Value ?? (element as any).SelectionCode ?? '';
                }
                // Search inside nested elements (Row, etc.)
                if (element.subSectionList?.length) {
                    for (const sub of element.subSectionList) {
                        const found = searchElements(sub?.ListOfFormSectionElements ?? []);
                        if (found !== null) return found;
                    }
                }
            }
            return null;
        };

        for (const section of sections) {
            for (const subSection of section.ListOfFormSubSections ?? []) {
                const found = searchElements(subSection.ListOfFormSectionElements ?? []);
                if (found !== null) return found;
            }
        }
        return '';
    };

    const collectSummaryFilters = (): { columns: string[]; value: string }[] => {
        const filters: { columns: string[]; value: string }[] = [];
        const programContainer = getRootComponent(field);

        const traverseElements = (elementsToSearch?: FormSectionElements[]) => {
            elementsToSearch?.forEach(element => {
                if ((element as any)?.Visible === false) {
                    return;
                }

                const typedElement = element as any;

                // Get column selection for filtering
                let columnSelection: string[] = [];

                // If dynamic column selector is configured, use it (and ONLY it)
                if (typedElement?.filterColumnSelectorId) {
                    const currentDropdownValue = getElementValue(typedElement.filterColumnSelectorId, programContainer);
                    if (currentDropdownValue) {
                        // Use stored columns if dropdown value matches, otherwise use current dropdown value
                        if (typedElement?._effectiveFilterColumns?.length &&
                            typedElement._effectiveFilterColumns.includes(currentDropdownValue)) {
                            columnSelection = typedElement._effectiveFilterColumns;
                        } else {
                            columnSelection = [currentDropdownValue];
                        }
                    }
                    // If dropdown is empty/cleared, columnSelection stays empty = NO filter
                    // Do NOT fall back to static configuration when dynamic selector is configured
                } else {
                    // No dynamic selector - use static configuration
                    columnSelection = Array.isArray(typedElement?.filterSummaryListColumns)
                        ? typedElement.filterSummaryListColumns
                        : (typedElement?.filterSummaryListColumn ? [typedElement.filterSummaryListColumn] : []);
                }

                if (typedElement?.filterSummaryListId === field.Id && columnSelection.length) {
                    const selectedValue = element.Value ?? typedElement.SelectionCode;
                    if (selectedValue !== null && selectedValue !== undefined && `${selectedValue}` !== "") {
                        filters.push({
                            columns: columnSelection,
                            value: String(selectedValue)
                        });
                    }
                }
                if (element.subSectionList?.length) {
                    element.subSectionList.forEach(sub => traverseElements(sub?.ListOfFormSectionElements));
                }
            });
        };

        traverseElements(field.parent?.ListOfFormSectionElements);

        const rootSections = programContainer?.ListOfFormSections ?? [];
        rootSections.forEach(section => {
            section?.ListOfFormSubSections?.forEach(subSection => {
                traverseElements(subSection?.ListOfFormSectionElements);
            });
        });

        return filters;
    };

    const matchesFilter = (record: any, filter: { columns: string[]; value: string }): boolean => {
        if (filter.value.includes('|')) {
            const parts = filter.value.split('|');
            const startDateStr = parts[0] || '';
            const endDateStr = parts[1] || '';
            const startDate = startDateStr ? new Date(startDateStr) : null;
            const endDate = endDateStr ? new Date(endDateStr) : null;
            if (startDate || endDate) {
                return filter.columns.some(columnName => {
                    const recordValue = record?.[columnName];
                    if (recordValue === null || recordValue === undefined) return false;

                    const recordDate = new Date(recordValue);
                    if (isNaN(recordDate.getTime())) return false;
                    if (startDate && endDate) {
                        return recordDate >= startDate && recordDate <= endDate;
                    } else if (startDate) {
                        return recordDate >= startDate;
                    } else if (endDate) {
                        return recordDate <= endDate;
                    }
                    return false;
                });
            }
        }

        const normalizedFilter = filter.value.toString().toLowerCase();
        return filter.columns.some(columnName => {
            const recordValue = record?.[columnName];
            if (recordValue === null || recordValue === undefined) return false;
            return String(recordValue).toLowerCase().includes(normalizedFilter);
        });
    };

    const processedData = useMemo(() => {
        if (!isClientSide) return allData;

        let result = [...allData];

        const activeFilters = collectSummaryFilters();
        if (activeFilters.length > 0) {
            result = result.filter(record =>
                activeFilters.every(filter => matchesFilter(record, filter))
            );
        }

        if (sortField) {
            result.sort((a, b) => {
                const aVal = a[sortField] ?? '';
                const bVal = b[sortField] ?? '';

                let comparison = 0;
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    comparison = aVal - bVal;
                } else {
                    comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
                }

                return sortOrder === 'asc' ? comparison : -comparison;
            });
        }

        return result;
    }, [allData, sortField, sortOrder, isClientSide, filterTrigger]);

    const effectiveTotalCount = isClientSide ? processedData.length : totalCount;
    const totalPages = Math.ceil(effectiveTotalCount / pageSize);
    const resultsSummaryText = useMemo(() => {
        const fieldAny = field as any;
        if (fieldAny?.ShowSearchResultsMessage !== true) {
            return null;
        }

        const activeFilters = collectSummaryFilters();
        const searchFilter = activeFilters.find(filter =>
            typeof filter?.value === 'string' &&
            filter.value.trim().length > 0 &&
            !filter.value.includes('|')
        );

        if (!searchFilter) {
            return null;
        }

        const searchValue = searchFilter.value.trim();
        const isSingular = effectiveTotalCount === 1;
        return isEng
            ? `${effectiveTotalCount} ${isSingular ? 'result' : 'results'} for ${searchValue}`
            : `${effectiveTotalCount} ${isSingular ? 'resultat' : 'resultats'} pour ${searchValue}`;
    }, [field, effectiveTotalCount, filterTrigger, isEng]);

    const currentData = useMemo(() => {
        if (isClientSide) {
            const start = currentPage * pageSize;
            return processedData.slice(start, start + pageSize);
        }
        return allData;
    }, [isClientSide, processedData, allData, currentPage, pageSize]);

    const resolvePagedApiUrl = useCallback((url: string, page: number, size: number): string => {
        const programContainer = getRootComponent(field);
        let resolvedUrl = url;

        // FIRST: Resolve form field placeholders (so they can override system placeholders)
        const placeholderPattern = /\{\{([^}]+)\}\}/g;
        const matches = resolvedUrl.match(placeholderPattern);

        if (matches) {
            const sections = programContainer?.ListOfFormSections ?? [];
            const findElementByName = (elements: any[], name: string): any => {
                for (const element of elements ?? []) {
                    if (element.Id === name || element.LoadBindName === name) {
                        return element;
                    }
                    if (element.subSectionList?.length) {
                        for (const sub of element.subSectionList) {
                            const found = findElementByName(sub.ListOfFormSectionElements, name);
                            if (found) return found;
                        }
                    }
                }
                return null;
            };

            matches.forEach(placeholder => {
                const fieldName = placeholder.replace(/\{\{|\}\}/g, '').trim();
                // Skip pagination and sorting placeholders that should be handled by resolveApiUrl
                if (['page', 'pageIndex', 'size', 'pageSize', 'limit', 'offset', 'sortField', 'sortBy', 'sortOrder', 'sortDirection'].includes(fieldName)) {
                    return;
                }
                let sourceField: any = findElementByName(field.parent?.ListOfFormSectionElements ?? [], fieldName);
                if (!sourceField) {
                    for (const section of sections) {
                        for (const subSection of section.ListOfFormSubSections ?? []) {
                            sourceField = findElementByName(subSection.ListOfFormSectionElements, fieldName);
                            if (sourceField) break;
                        }
                        if (sourceField) break;
                    }
                }

                if (sourceField) {
                    const value = sourceField.Value ?? sourceField.SelectionCode ?? '';
                    // Custom encoding: encode everything except & and =
                    const customEncode = (str: string) => {
                        return encodeURIComponent(str)
                            .replace(/%26/g, '&')  // Unescape &
                            .replace(/%3D/g, '='); // Unescape =
                    };
                    resolvedUrl = resolvedUrl.replace(placeholder, customEncode(String(value)));
                }
            });
        }

        // SECOND: Resolve remaining system placeholders (page, userId, etc.)
        resolvedUrl = resolveApiUrl(resolvedUrl, {
            page,
            pageSize: size,
            sortField,
            sortOrder,
            programContainer
        });

        return resolvedUrl;
    }, [field, sortField, sortOrder]);

    const findFieldValue = (programContainer: ProgramContainer, fieldId: string): string => {
        let value = '';
        const sections = programContainer?.ListOfFormSections ?? [];

        const searchElements = (elements: FormSectionElements[]): boolean => {
            for (const element of elements) {
                if (element.Id === fieldId) {
                    value = element.Value ?? element.SelectionCode ?? '';
                    return true;
                }
                if (element.subSectionList?.length) {
                    for (const sub of element.subSectionList) {
                        if (sub.ListOfFormSectionElements && searchElements(sub.ListOfFormSectionElements)) {
                            return true;
                        }
                    }
                }
            }
            return false;
        };

        for (const section of sections) {
            for (const subSection of section.ListOfFormSubSections ?? []) {
                if (searchElements(subSection.ListOfFormSectionElements ?? [])) {
                    return value;
                }
            }
        }
        return value;
    };

    const extractDataFromPath = (response: any, path: string): any => {
        if (!path) return response;
        const keys = path.split('.');
        let result = response;
        for (const key of keys) {
            if (result && typeof result === 'object' && key in result) {
                result = result[key];
            } else {
                return null;
            }
        }
        return result;
    };

    const getFieldValue = (fieldId: string): string => {
        const programContainer = getRootComponent(field);
        const sections = programContainer?.ListOfFormSections ?? [];

        const searchElements = (elements: FormSectionElements[]): string | null => {
            for (const element of elements ?? []) {
                if (element.Id === fieldId) {
                    return element.Value ?? (element as any).SelectionCode ?? '';
                }
                // Search inside nested elements (Row, etc.)
                if (element.subSectionList?.length) {
                    for (const sub of element.subSectionList) {
                        const found = searchElements(sub?.ListOfFormSectionElements ?? []);
                        if (found !== null) return found;
                    }
                }
            }
            return null;
        };

        for (const section of sections) {
            for (const subSection of section.ListOfFormSubSections ?? []) {
                const found = searchElements(subSection.ListOfFormSectionElements ?? []);
                if (found !== null) return found;
            }
        }
        return '';
    };

    const updateLinkedSearchAutocompleteOptions = (records: any[]) => {
        if (!Array.isArray(records)) return;
        const programContainer = getRootComponent(field);
        const sections = programContainer?.ListOfFormSections ?? [];

        const registerOptionsForElement = (searchElement: FormSectionElements) => {
            const searchConfig = searchElement as any;

            // Check for dynamic column selector first
            let columnNames: string[] = [];
            if (searchConfig?.filterColumnSelectorId) {
                const selectedColumn = getFieldValue(searchConfig.filterColumnSelectorId);
                if (selectedColumn) {
                    columnNames = [selectedColumn];
                }
            }

            // Fall back to static configuration if no dynamic selector or no selection
            if (!columnNames.length) {
                columnNames = Array.isArray(searchConfig?.filterSummaryListColumns) && searchConfig?.filterSummaryListColumns.length
                    ? searchConfig.filterSummaryListColumns
                    : (searchConfig?.filterSummaryListColumn ? [searchConfig.filterSummaryListColumn] : []);
            }

            if (!columnNames.length) {
                return;
            }

            // Store the effective column names for filtering to ensure consistency
            searchConfig._effectiveFilterColumns = columnNames;

            const optionsMap = new Map<string, LOVValues>();

            // For Dropdown elements, add "All" option if configured
            const isDropdown = searchElement.Datatype === 'Dropdown';
            if (isDropdown && searchConfig?.filterAllOptionLabel) {
                const allLov = new LOVValues();
                allLov.code = '';
                allLov.description = searchConfig.filterAllOptionLabel || 'All';
                allLov.FRADescription = searchConfig.filterAllOptionLabelFRA || searchConfig.filterAllOptionLabel || 'Tous';
                allLov.parent = searchElement;
                allLov.Sequence = -1; // Ensure it appears first
                optionsMap.set('__ALL__', allLov);
            }

            const pushValue = (raw: any) => {
                if (raw === null || raw === undefined) return;
                const stringValue = String(raw).trim();
                if (!stringValue) return;
                if (!optionsMap.has(stringValue)) {
                    const lov = new LOVValues();
                    lov.code = stringValue;
                    lov.description = stringValue;
                    lov.FRADescription = stringValue;
                    lov.parent = searchElement;
                    optionsMap.set(stringValue, lov);
                }
            };

            records.forEach(record => {
                columnNames.forEach(columnName => {
                    pushValue(record?.[columnName]);
                });
            });

            // Convert to array and sort (keeping "All" option first)
            const lovArray = Array.from(optionsMap.values());
            if (isDropdown) {
                lovArray.sort((a, b) => {
                    // Keep "All" option first (identified by Sequence = -1)
                    if (a.Sequence === -1) return -1;
                    if (b.Sequence === -1) return 1;
                    // Sort remaining options alphabetically
                    return (a.description || '').localeCompare(b.description || '');
                });
            }

            searchElement.ListOfLOVValues = lovArray;

            // Trigger dropdown re-render if callback is available
            if (isDropdown && searchConfig._optionsUpdated) {
                searchConfig._optionsUpdated();
            }
        };

        const traverseElements = (elementsToSearch?: FormSectionElements[]) => {
            elementsToSearch?.forEach(element => {
                const typedElement = element as any;
                if (typedElement?.filterSummaryListId === field.Id) {
                    registerOptionsForElement(element);
                }
                if (element.subSectionList?.length) {
                    element.subSectionList.forEach(sub => traverseElements(sub?.ListOfFormSectionElements));
                }
            });
        };

        traverseElements(field.parent?.ListOfFormSectionElements);

        sections.forEach(section => {
            section?.ListOfFormSubSections?.forEach(subSection => {
                traverseElements(subSection?.ListOfFormSectionElements);
            });
        });
    };

    const fetchData = useCallback(async (page: number) => {
        const programContainer = getRootComponent(field);

        if (!field.LovAPI) {
            setAllData([]);
            setTotalCount(0);
            setIsLoading(false);
            return;
        }

        let api = resolvePagedApiUrl(field.LovAPI, page, pageSize);

        const isExternalUrl = api.startsWith("http");

        if (!isExternalUrl) {
            api = programContainer.APIBasePath?.replace(/\/+$/, '') + '/' + api.replace(/^\/+/, '');
        }

        const urlWithoutProtocol = api.replace(/^https?:\/\//, '');
        if (urlWithoutProtocol.includes('//')) {
            console.log('[PagedSummaryList] Skipping request with unresolved placeholder:', api);
            setIsLoading(false);
            return;
        }

        // Also check instance-level ref for same component instance
        if (isFetchingRef.current && lastFetchedUrlRef.current === api) {
            console.log('[PagedSummaryList] Skipping duplicate request (in-flight):', api);
            return;
        }

        const apiKey: APIKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue)
            ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
            : null;

        let token = null;
        if (programContainer.AuthRequired) {
            token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
        }

        const headers = setBaseHeaders('application/json', token, apiKey);

        const configs = isExternalUrl
            ? { headers }
            : { headers, withCredentials: true };

        try {
            isFetchingRef.current = true;
            lastFetchedUrlRef.current = api;
            setIsLoading(true);
           
            let response;
            if ((field as any).LovMethod?.toLowerCase() === 'post') {
                let payload = (field as any).LovPayload;
                if (payload && typeof payload === 'object') {
                    // Resolve placeholders in payload values
                    payload = JSON.parse(
                        JSON.stringify(payload).replace(/\{\{([^}]+)\}\}/g, (_: string, key: string) => {
                            if (key === 'organizationId') {
                                const v = sessionStorage.getItem('organizationId');
                                if (v) return v;
                                try {
                                    const profile = JSON.parse(sessionStorage.getItem('userProfile') || '{}');
                                    if (profile.organizationId) return profile.organizationId;
                                } catch (e) {}
                            }
                            if (key === 'operatorNumber') {
                                try {
                                    const profile = JSON.parse(sessionStorage.getItem('userProfile') || '{}');
                                    if (profile.operatorNumber) return profile.operatorNumber;
                                } catch (e) {}
                            }
                            // Fallback: try resolveApiUrl-style resolution
                            return `{{${key}}}`;
                        })
                    );
                }
                response = await axios.post(api, payload || {}, configs);
            } else {
                response = await axios.get(api, configs);
            }

            const dataPath = field.DataPath || '';
            let extractedData = dataPath ? extractDataFromPath(response.data, dataPath) : response.data;

            // Support DataTransformScript to normalize fetched data
            if ((field as any).DataTransformScript && Array.isArray(extractedData)) {
                try {
                    const transformFn = eval(`(${(field as any).DataTransformScript})`);
                    extractedData = extractedData.map((item: any) => transformFn(item));
                } catch (e) {
                    console.error('[PagedSummaryList] DataTransformScript error:', e);
                }
            }

            // Debug logging
            console.log('[PagedSummaryList] API Response:', {
                dataPath,
                responseKeys: Object.keys(response.data || {}),
                extractedData: extractedData,
                isArray: Array.isArray(extractedData),
                length: Array.isArray(extractedData) ? extractedData.length : 'N/A'
            });

            let dataToSet: any[] = [];

            if (Array.isArray(extractedData)) {
                dataToSet = extractedData;
            } else if (extractedData && Array.isArray(extractedData.content)) {
                dataToSet = extractedData.content;
            } else if (extractedData && Array.isArray(extractedData.items)) {
                dataToSet = extractedData.items;
            }

            if (Array.isArray(dataToSet)) {
                dataToSet.forEach((item: any) => applyNameSentenceCase(item));
            }

            // Store on field IMMEDIATELY for other instances (React StrictMode)
            // Use API URL as key to avoid stale data from different requests
            (field as any)._fetchedDataUrl = api;
            (field as any)._fetchedData = dataToSet;
            (field as any)._fetchedTotalCount = dataToSet.length;

            updateLinkedSearchAutocompleteOptions(dataToSet);
            setAllData(dataToSet);
            if (isClientSide) {
                setTotalCount(dataToSet.length);
            }

            if (!isClientSide) {
                if (field.IsLastPath) {
                    const isLast = extractDataFromPath(response.data, field.IsLastPath);
                    setIsLastPage(isLast === true);
                } else {
                    const totalPath = field.TotalCountPath || 'totalCount';
                    let total = extractDataFromPath(response.data, totalPath);

                    if (total === null || total === undefined) {
                        total = response.data.totalElements ??
                            response.data.total ??
                            response.data.totalCount ??
                            response.data.count ??
                            0;
                    }

                    setTotalCount(Number(total) || 0);
                }
            }
        } catch (error: any) {
            if (error.response?.status === 401 || error.response?.status === 403) {
                setErrorVisible(true);
            }
            console.error("Error fetching paged data:", error);
            setAllData([]);
            setTotalCount(0);
        } finally {
            isFetchingRef.current = false;
            setIsLoading(false);
        }
    }, [field, pageSize, isClientSide, resolvePagedApiUrl]);

    useEffect(() => {
        (field as any)._allData = allData;
        (field as any)._totalCount = effectiveTotalCount;
        (field as any)._currentPage = currentPage;
        (field as any)._setCurrentPage = setCurrentPage;
        (field as any)._triggerFilter = () => setFilterTrigger(prev => prev + 1);
        // Expose function to rebuild autocomplete options - called by SearchAutocomplete when column selector changes
        (field as any)._rebuildAutocompleteOptions = () => {
            if (allData.length > 0) {
                updateLinkedSearchAutocompleteOptions(allData);
            }
        };
        // Expose refresh function for external components to trigger search
        (field as any)._refreshData = () => {
            if (isClientSide) {
                fetchData(0);
                setCurrentPage(0);
            } else {
                fetchData(currentPage);
            }
        };
    }, [allData, effectiveTotalCount, currentPage, field, isClientSide, fetchData]);

    useEffect(() => {
        const currentFilters = JSON.stringify(collectSummaryFilters());
        const lastFilters = (field as any)._lastFilters;
        if (lastFilters !== currentFilters) {
            (field as any)._lastFilters = currentFilters;
            if (lastFilters !== undefined) {
                setFilterTrigger(prev => prev + 1);
                setCurrentPage(0);
                // Rebuild autocomplete options when column selector changes
                // This is needed because updateLinkedSearchAutocompleteOptions was only called
                // at data fetch time, but the column selector might not have been selected yet
                if (allData.length > 0) {
                    updateLinkedSearchAutocompleteOptions(allData);
                }
            }
        }
    }, [field, allData]);

    useEffect(() => {
        if (!isClientSide) return;

        if (!field.RefreshOnLoad) {
            const expectedUrl = resolvePagedApiUrl(field.LovAPI || '', 0, pageSize);
            const fetchedUrl = (field as any)._fetchedDataUrl;
            const fetchedData = (field as any)._fetchedData;

            if (fetchedUrl === expectedUrl && Array.isArray(fetchedData) && fetchedData.length > 0) {
                console.log('[PagedSummaryList] Using cached data from field:', fetchedData.length, 'items');
                setAllData(fetchedData);
                setTotalCount((field as any)._fetchedTotalCount ?? fetchedData.length);
                setIsLoading(false);
                return;
            }
        }

        fetchData(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isClientSide) {
            fetchData(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sortField, sortOrder, isClientSide]);

    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleSort = (fieldName: string) => {
        if (sortField === fieldName) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(fieldName);
            setSortOrder('asc');
        }
        if (!isClientSide) {
            setCurrentPage(0);
        }
    };

     const handleKeyDown = (event: React.KeyboardEvent, item: any) => {
         if (event.key === 'Enter' || event.key === ' ') {
             event.preventDefault();
             handleSort(item);
         }
     };

    const handleDownload = () => {
        const dataToExport = isClientSide ? processedData : currentData;
        if (dataToExport.length === 0) return;

        const headers = elements.map(ele => isEng ? ele.ENUTitle : ele.FRATitle).join(',');
        const rows = dataToExport.map(record => {
            return elements.map(col => {
                const value = record[col.LoadBindName] ?? '';
                const escaped = String(value).replace(/"/g, '""');
                return `"${escaped}"`;
            }).join(',');
        }).join('\n');

        const currentDate = formatDateWithCustomFormat(new Date(), 'YYYY-MM-DD');
        const csvContent = `${headers}\n${rows}`;
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${field.DownloadTitle}${currentDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleViewEvent = (column: FormSectionElements, event: React.MouseEvent, record: any) => {
        event.preventDefault();

        // If ShowPopup is enabled, show confirmation popup first
        if (column.ShowPopup) {
            setPopupColumn(column);
            setPopupRecord(record);
            setShowPopup(true);
            return;
        }

        // Otherwise execute EventScript directly
        const functions = {
            getRootComponent: (col: FormSectionElements) => getRootComponent(col),
            callbackStepIndex: (index: number) => { callbackStepIndex?.(index); },
        };
        if (column.EventScript && column.EventScript.trim()) {
            const customJSFunc = eval(`(${column.EventScript})`);
            customJSFunc(column, record, functions);
        }
    };

    const handlePopupCancel = () => {
        setShowPopup(false);
        setPopupColumn(null);
        setPopupRecord(null);
    };

    const handlePopupConfirm = async () => {
        if (!popupColumn || !popupRecord) return;

        const programContainer = getRootComponent(field);
        const functions = {
            getRootComponent: (col: FormSectionElements) => getRootComponent(col),
            callbackStepIndex: (index: number) => { callbackStepIndex?.(index); },
        };

        // If ConfirmAPI is specified, make the API request
        if (popupColumn.ConfirmAPI) {
            try {
                setIsClickLoading(true);
                let confirmUrl = resolveClickUrl(popupColumn.ConfirmAPI, popupRecord);
                const isExternalUrl = confirmUrl.startsWith('http://') || confirmUrl.startsWith('https://');

                if (!isExternalUrl) {
                    confirmUrl = programContainer.APIBasePath?.replace(/\/+$/, '') + '/' + confirmUrl.replace(/^\/+/, '');
                }

                const apiKey: APIKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue)
                    ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
                    : null;

                let token = null;
                if (programContainer.AuthRequired) {
                    token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
                }

                const headers = setBaseHeaders('application/json', token, apiKey);
                const configs = isExternalUrl
                    ? { headers }
                    : { headers, withCredentials: true };

                const method = (popupColumn.ConfirmAPIMethod || 'delete').toLowerCase();
                if (method === 'post') {
                    await axios.post(confirmUrl, {}, configs);
                } else if (method === 'put') {
                    await axios.put(confirmUrl, {}, configs);
                } else {
                    await axios.delete(confirmUrl, configs);
                }

                // Refresh the data after successful API call
                fetchData(currentPage);

            } catch (error: any) {
                console.error("Error calling confirm API:", error);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    setErrorVisible(true);
                }
            } finally {
                setIsClickLoading(false);
            }
        }

        // Execute EventScript if specified (check for non-empty string)
        if (popupColumn.EventScript && popupColumn.EventScript.trim()) {
            const customJSFunc = eval(`(${popupColumn.EventScript})`);
            customJSFunc(popupColumn, popupRecord, functions);
        }

        handlePopupCancel();
    };

    const resolveClickUrl = (url: string, record: any): string => {
        if (!url) return '';
        const programContainer = getRootComponent(field);
        let resolvedUrl = resolveApiUrl(url, { programContainer });
        const placeholders: string[] = resolvedUrl.match(/\{\{([^}]+)\}\}/g) || [];
        placeholders.forEach((placeholder: string) => {
            const fieldName = placeholder.replace(/\{\{|\}\}/g, '').trim();
            if (['operatorNumber', 'serviceId', 'userId', 'USERID', 'organizationId'].includes(fieldName)) {
                return;
            }
            const value = record[fieldName] ?? '';
            resolvedUrl = resolvedUrl.replace(placeholder, encodeURIComponent(String(value)));
        });
        return resolvedUrl;
    };

    const handleColumnClick = async (column: FormSectionElements, event: React.MouseEvent, record: any) => {
        event.preventDefault();

        const programContainer = getRootComponent(field);
        const functions = {
            getRootComponent: (col: FormSectionElements) => getRootComponent(col),
            callbackStepIndex: (index: number) => { callbackStepIndex?.(index); },
            callback: callback,
            record: record,
        };

        if (column.EventScript && column.EventScript.trim()) {
            const customJSFunc = eval(`(${column.EventScript})`);
            customJSFunc(column, record, functions);
            return;
        }

        if (column.ClickUrl) {
            if (column.StoreClickedRowData) {
                const tableKey = String(field.LoadBindName || field.Id || field.ENUTitle || 'pagedSummaryList');
                const container = programContainer as any;
                if (!container.clickedRowDataByTable) {
                    container.clickedRowDataByTable = {};
                }
                // store clicked row data on program container for next detail pages
                container.clickedRowDataByTable[tableKey] = record;
                container.clickedRowData = record;
            }

            const resolvedUrl = resolveClickUrl(column.ClickUrl, record);
            const isExternalUrl = resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://');

            try {
                setIsClickLoading(true);
                let apiUrl = resolvedUrl;
                if (!isExternalUrl) {
                    apiUrl = programContainer.APIBasePath?.replace(/\/+$/, '') + '/' + resolvedUrl.replace(/^\/+/, '');
                }

                const apiKey: APIKey = (programContainer.ApiKeyKey && programContainer.ApiKeyValue)
                    ? { key: programContainer.ApiKeyKey, value: programContainer.ApiKeyValue }
                    : null;

                let token = null;
                if (programContainer.AuthRequired) {
                    token = SessionStorageService.getAuthToken(programContainer.AuthRequired) ?? programContainer.Profile?.token;
                }

                const headers = setBaseHeaders('application/json', token, apiKey);
                const configs = isExternalUrl
                    ? { headers }
                    : { headers, withCredentials: true };

                const response = await axios.get(apiUrl, configs);

                if (response.data) {
                    // Set Entity and auto-populate form fields using DataUtils.assignFormValue
                    programContainer.Entity = response.data;
                    applyNameSentenceCase(programContainer.Entity);
                    DataUtils.assignFormValue(programContainer);

                    // Also store in ClickApiResponseField if specified
                    if (column.ClickApiResponseField) {
                        const sections = programContainer?.ListOfFormSections ?? [];
                        for (const section of sections) {
                            for (const subSection of section.ListOfFormSubSections ?? []) {
                                for (const element of subSection.ListOfFormSectionElements ?? []) {
                                    if (element.Id === column.ClickApiResponseField || element.LoadBindName === column.ClickApiResponseField) {
                                        element.apiResponse = response.data;
                                        element.Value = JSON.stringify(response.data);
                                        break;
                                    }
                                }
                            }
                        }
                        (programContainer as any)[column.ClickApiResponseField] = response.data;
                    }
                }

                if (column.ClickNextStep !== undefined && column.ClickNextStep !== null) {
                    callbackStepIndex?.(column.ClickNextStep);
                }

                if (callback) {
                    callback(programContainer);
                }

            } catch (error: any) {
                console.error("Error calling API on column click:", error);
                if (error.response?.status === 401 || error.response?.status === 403) {
                    setErrorVisible(true);
                }
            } finally {
                setIsClickLoading(false);
            }
        }
    };

    const truncateCellValue = (value: string, column: FormSectionElements): { display: string; full: string | null } => {
        const raw = value ?? '';
        const maxLen = typeof column.maxLength === 'number' && column.maxLength > 0
            ? column.maxLength
            : null;
        const displayLen = typeof column.maxDisplayLength === 'number' && column.maxDisplayLength > 0
            ? column.maxDisplayLength
            : null;

        const capped = maxLen !== null && raw.length > maxLen ? raw.slice(0, maxLen) : raw;
        if (displayLen !== null && capped.length > displayLen) {
            return { display: capped.slice(0, displayLen) + '…', full: capped };
        }

        return { display: capped, full: null };
    };

    const renderCellContent = (column: FormSectionElements, record: any, colIdx: number) => {
        let cellValue: string = '';

        switch (column.Datatype) {
            case "Text":
                cellValue = isEng ? record[column.LoadBindName] : record[column.LoadBindBaseFRA] ??  record[column.LoadBindNameFRA] ?? record[column.LoadBindName] ?? '';
                if (!cellValue?.toString().trim().length) {
                    cellValue = isEng
                        ? (column.ToDisplayWhenEmpty || '')
                        : (column.ToDisplayWhenEmptyFRA || '');
                }
                break;

            case "Date":
                if (record[column.LoadBindName]?.toString().trim().length) {
                    cellValue = column.DateDisplayFormat
                        ? formatDateWithCustomFormat(record[column.LoadBindName], column.DateDisplayFormat)
                        : formatDateCustom(record[column.LoadBindName]);
                }
                if (!cellValue || cellValue === "0001-01-01") {
                    cellValue = isEng ? column.ToDisplayWhenEmpty : column.ToDisplayWhenEmptyFRA;
                }
                break;

            default:
                cellValue = record[column.LoadBindName] ?? '';
        }

        const { display: displayValue, full: fullValue } = truncateCellValue(cellValue?.toString() ?? '', column);

        if (column.IsClickable) {
        let ariaLabelText = "";
        if (column.AccessibilityLabel) {
            ariaLabelText = column.AccessibilityLabel.replace(/{(.*?)}/g, (_,field) => record[field]?.toString() ?? "");
        }
            return (
                <td key={colIdx} title={fullValue ?? undefined}>
                    <a
                        href="#"
                        onClick={(event) => handleColumnClick(column, event, record)}
                        className="paged-summary-list__clickable-link"
                        aria-label={ariaLabelText ? ariaLabelText : ""}
                    >
                        {displayValue}
                    </a>
                </td>
            );
        }

        return <td key={colIdx} title={fullValue ?? undefined}>{displayValue}</td>;
    };

    const handleHeaderLinkClick = (field: any) => {
        if (field?.HeaderLinkStepperScript) {
            executeStepperScript(field.HeaderLinkStepperScript);
        }
    }

    const executeStepperScript = (script: string) => {
        const customJSFunc = eval(`(${script})`);
        customJSFunc((destination) => {
            if (typeof destination === "string") {
                const root = getRootComponent(field);
                const foundSection = root.ListOfFormSections.find(section => section.ENUTitle == destination);
                const indexNum = root.ListOfFormSections.indexOf(foundSection);
                callbackStepIndex(indexNum)
            } else {
                callbackStepIndex(destination);
            }
        });
    }

    const handleBottomButtonClick = (field: any) => {
        if (field?.BottomButtonStepperScript) {
            executeStepperScript(field.BottomButtonStepperScript);
        }
    }

    const handleBottomRecaptchaButtonClick = (field: any) => {
        if (field?.BottomButtonStepperScript) {
            executeStepperScript(field.BottomButtonStepperScript);
        }
    }

    const renderBottomButton = () => {
        return (
            <div className='paged-summary-list__bottom-button-center' key={"button_centered_" + field.Sequence}>
                <div key={"bottomButton_" + field.Sequence}>
                    {!field.BottomButtonHasRecaptcha ? (
                        <button
                            id={`bottomButton_${field.Sequence}`}
                            className={field.BottomButtonClassName}
                            title={isEng ? field.BottomButtonLabel : field.BottomButtonLabelFRA}
                            onClick={(e) => {
                                e.preventDefault();
                                handleBottomButtonClick(field);
                            }}
                        >
                            {isEng ? field.BottomButtonLabel : field.BottomButtonLabelFRA}
                        </button>
                    ) : (
                        <ReCaptchaButton
                            id={`recaptchBottomBbutton_${field.Sequence}`}
                            title={isEng ? field.BottomButtonLabel : field.BottomButtonLabelFRA}
                            buttonHandler={handleBottomRecaptchaButtonClick}
                        >
                            {isEng ? field.BottomButtonLabel : field.BottomButtonLabelFRA}
                        </ReCaptchaButton>
                    )}
                </div>
            </div>
        );
    }

    const renderPagination = () => {
        // IsLastPath mode: show prev/next only, driven by the isLastPage flag from the API.
       if (field.IsLastPath && !isClientSide) {
            const hasPrev = currentPage > 0;
            const hasNext = !isLastPage;
            if (!hasPrev && !hasNext) return null;
            return (
                <nav className="paged-summary-list__pagination" aria-label="Incident reports pagination">
                    {hasPrev && (
                        <button
                            key="prev"
                            className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                            aria-label="Go to previous page"
                            onClick={() => setCurrentPage(currentPage - 1)}
                        >
                            {isEng ? 'Previous page' : 'Page précédente'}
                        </button>
                    )}
                    {hasNext && (
                        <button
                            key="next"
                            className="ontario-button ontario-button--primary paged-summary-list__pagination-next"
                            aria-label="Go to next page"
                            onClick={() => setCurrentPage(currentPage + 1)}
                        >
                            {isEng ? 'Next page' : 'Page suivante'}
                        </button>
                    )}
                </nav>
            );       
       
       }

        if (totalPages <= 1) return null;

        const buttons: JSX.Element[] = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(0, endPage - maxVisiblePages + 1);
        }

        if (currentPage > 0) {
            buttons.push(
                <button
                    key="prev"
                    className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                    aria-label="Go to previous page"
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    {isEng ? 'Previous page' : 'Page précédente'}
                </button>
             );      
        }

        if (startPage > 0) {
            buttons.push(
                <button
                    key={0}
                    className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                    aria-label="Go to page 1"
                    onClick={() => handlePageChange(0)}
                >
                    1
                </button>
            );
            buttons.push(
                <span key="ellipsis-start" className="paged-summary-list__pagination-ellipsis" aria-hidden="true">...</span>
            );
        }

        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    className={`ontario-button paged-summary-list__pagination-page ${i === currentPage ? 'ontario-button--primary' : 'ontario-button--secondary'}`}
                    onClick={() => handlePageChange(i)}
                    aria-current={i === currentPage ? 'page' : undefined}
                    aria-label={i === currentPage ? `Page ${currentPage + 1}, current page` : `Go to page ${i + 1}`}
                >
                    {i + 1}
                </button>
            );
        }

        if (endPage < totalPages - 1) {
            buttons.push(
                <span key="ellipsis-end" className="paged-summary-list__pagination-ellipsis" aria-hidden="true">...</span>
            );
            buttons.push(
                <button
                    key={totalPages - 1}
                    className="ontario-button ontario-button--secondary paged-summary-list__pagination-page"
                    aria-label={`Go to page ${totalPages}`}
                    onClick={() => handlePageChange(totalPages - 1)}
                >
                    {totalPages}
                </button>
            );
        }

        if (currentPage < totalPages - 1) {
            buttons.push(
                <button
                    key="next"
                    className="ontario-button ontario-button--primary paged-summary-list__pagination-next"
                    aria-label="Go to next page"
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    {isEng ? 'Next page' : 'Page suivante'}
                </button>
            );      
    }

        return (
            <nav className="paged-summary-list__pagination" aria-label="Incident reports pagination">
                {buttons}
            </nav>
        );
    };

    FormValidationUtils.runJavascript(field);

    if (!isLazyLoading && isLoading && hasMounted && allData.length === 0) {
        return <Processing />;
    }

    const caption = isEng ? field.ENUTitle : field.FRATitle;
    const HtmlToReactParser = require('html-to-react').Parser;
    const htmlToReactParser = new HtmlToReactParser();

    const getSortIcon = (columnName: string) => {
        if (sortField !== columnName) {
            return <span className="paged-summary-list__sort-icon">&#8597;</span>;
        }
        return sortOrder === 'asc'
            ? <span className="paged-summary-list__sort-icon">&#8593;</span>
            : <span className="paged-summary-list__sort-icon">&#8595;</span>;
    };

    return (
        <>
            {isClickLoading && <Processing />}
            {errorVisible && (
                <ErrorAlertComponent
                    bodyMsg={isEng
                        ? "You do not have permission to view this data."
                        : "Vous n'avez pas la permission de consulter ces données."}
                    title={isEng ? "Insufficient Permission" : "Permission insuffisante"}
                    isEng={isEng}
                    Visible={true}
                />
            )}

            <div className="paged-summary-list__header">
                {field.ShowTotalCount && <div className="paged-summary-list__total">
                    {resultsSummaryText ?? `${isEng ? 'Total' : 'Total'}: ${effectiveTotalCount}`}
                </div>}
                {field.ShowDownloadButton !== false && (
                    <button
                        className="ontario-button ontario-button--secondary"
                        onClick={handleDownload}
                        disabled={currentData.length === 0}
                    >
                        {isEng ? 'Download to Excel' : 'Télécharger vers Excel'}
                    </button>
                )}
            </div>

            <div className="paged-summary-list">
                <div className="ontario-table-container ontario-small-12 ontario-medium-12 ontario-large-12">
                    <div className="ontario-table-scroll--wrapper">
                        <div className="ontario-table-scroll--div"></div>
                    </div>
                    <div className="ontario-table-div">
                        {field.SummaryType === 'SummaryList' && <div className='paged-summary-list__summary-title'>
                            <h3 className='paged-summary-list__summary-caption'>{caption}</h3>
                            <div id={"table-header-element-split_" + field.Sequence} className='paged-summary-list__summary-caption-filler'>
                                <p className='paged-summary-list__caption-filler'></p>
                                {(field.ShowHeaderLink && (currentData.length > 0 || field.ShowLinkWhenEmpty)) &&
                                    <button id={"table-header-link_" + field.Sequence}
                                        title="Edit Call volume details"
                                        className='paged-summary-list__summary-header-link'
                                        onClick={(event) => handleHeaderLinkClick(field)}>
                                        <span className='paged-summary-list__table-header-link-text'>
                                            {field.HeaderLinkText}
                                        </span>
                                    </button>
                                }
                            </div>
                        </div>}
                        {field.SummaryType === 'Table' && <div>
                            {(field.ShowHeaderLink && (currentData?.length > 0 || field.ShowLinkWhenEmpty)) && <div className='paged-summary-list__table-title-link'>
                                <h3 className='paged-summary-list__summary-caption'></h3>
                                <div id={"table-header-element-split_" + field.Sequence} className='paged-summary-list__summary-caption-filler'>
                                    <p className='paged-summary-list__caption-filler'></p>
                                    {(field.ShowHeaderLink && (currentData.length > 0 || field.ShowLinkWhenEmpty)) &&
                                        <button id={"table-header-link_" + field.Sequence}
                                            title="Edit Call volume details"
                                            className='paged-summary-list__table-header-link'
                                            onClick={() => handleHeaderLinkClick(field)}>
                                            <span className='paged-summary-list__table-header-link-text'>
                                                {field.HeaderLinkText}
                                            </span>
                                        </button>
                                    }
                                </div>
                            </div>}
                             <div className='paged-summary-list__table-title'>
                              {/* <div className={field.ShowTitleBackground ? 'paged-summary-list__table-title' : 'paged-summary-list__table-title_without_background'}> */}
                                <h3 className='paged-summary-list__summary-caption'>{caption}</h3>
                                <div id={"table-header-element-split_" + field.Sequence} className='paged-summary-list__summary-caption-filler'>
                                    <p className='paged-summary-list__caption-filler'></p>
                                </div>
                                {/* <div className='paged-summary-list__summary-message'>{htmlToReactParser.parse(isEng ? field.SummaryListMessage : field.SummaryListMessageFRA)}</div> */}
                            </div> 
                        </div>}
                        <table className="ontario-table--full-container-width">
                            {field.SummaryType !== 'SummaryList' && <thead>
                                <tr>
                                    {(() => {
                                        const headers: JSX.Element[] = [];
                                        const buttonColumns = elements.filter(ele => ele.Datatype === "Button");
                                        const nonButtonColumns = elements.filter(ele => ele.Datatype !== "Button");
                                        let buttonHeaderRendered = false;

                                        elements.forEach((ele, eleIdx) => {
                                            if (ele.Datatype === "Button") {
                                                if (!buttonHeaderRendered) {
                                                    headers.push(
                                                        <th key="actions_header" scope="col">
                                                            {isEng ? 'Actions' : 'Actions'}
                                                        </th>
                                                    );
                                                    buttonHeaderRendered = true;
                                                }
                                            } else {
                                                const isSortable = ele.LoadBindName && ele.AllowSorting !== false;
                                                headers.push(
                                                    <th
                                                        key={`header_${ele.Id || eleIdx}`}
                                                        scope="col"
                                                        aria-sort={sortField !== ele.LoadBindName ? 'none' : 
                                                            sortOrder === 'asc' ? 'ascending': 'descending'}
                                                        className={isSortable ? 'paged-summary-list__sortable-header' : ''}
                                                        tabIndex={0}
                                                        onClick={() => isSortable && handleSort(ele.LoadBindName)}
                                                        onKeyDown={(e) => isSortable && handleKeyDown(e, ele.LoadBindName)}
                                                    >
                                                        {isEng ? ele.ENUTitle : ele.FRATitle}
                                                        {isSortable && getSortIcon(ele.LoadBindName)}
                                                    </th>
                                                );
                                            }
                                        });
                                        return headers;
                                    })()}
                                </tr>
                            </thead>}
                            <tbody>
                                {isLazyLoading && isLoading && allData.length === 0 ? (
                                    <tr>
                                        <td colSpan={elements.length || 1} className="paged-summary-list__empty-message">
                                            <div className="ontario-loading-indicator">
                                                <svg className="ontario-loading-indicator__spinner" viewBox="25 25 50 50" xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="50" cy="50" r="20" fill="none" strokeWidth="4"></circle>
                                                </svg>
                                                <p>{isEng ? 'Loading...' : 'Chargement...'}</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : currentData.length > 0 ? (
                                    currentData.map((record, idx) => (
                                        <tr key={idx}>
                                            {(() => {
                                                const cells: JSX.Element[] = [];
                                                const buttonColumns = elements.filter(col => col.Datatype === "Button");
                                                let buttonCellRendered = false;

                                                elements.forEach((column, colIdx) => {
                                                    if (column.Datatype === "Button") {
                                                        if (!buttonCellRendered) {
                                                            cells.push(
                                                                <td key="actions_cell">
                                                                    {buttonColumns.map((btnCol, btnIdx) => (
                                                                        <span key={btnIdx}>
                                                                            {btnIdx > 0 && <span style={{ margin: '0 0.5rem' }}>|</span>}
                                                                            <a
                                                                                href="#"
                                                                                onClick={(event) => handleViewEvent(btnCol, event, record)}
                                                                            >
                                                                                {isEng ? btnCol.ENUTitle : btnCol.FRATitle}
                                                                            </a>
                                                                        </span>
                                                                    ))}
                                                                </td>
                                                            );
                                                            buttonCellRendered = true;
                                                        }
                                                    } else {
                                                        cells.push(renderCellContent(column, record, colIdx));
                                                    }
                                                });
                                                return cells;
                                            })()}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={elements.length} className="paged-summary-list__empty-message">
                                            {htmlToReactParser.parse(
                                                isEng
                                                    ? (field.TableEmptyMessage || "No records found.")
                                                    : (field.TableEmptyMessageFRA || "Aucun enregistrement trouvé.")
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {field.ShowBottomButton && currentData.length > 0 && renderBottomButton()}

            {field.ShowPagination !== false && renderPagination()}

            {showPopup && popupColumn && (
                <CustomPopup
                    isVisible={showPopup}
                    title={isEng ? popupColumn.PopupTitle : popupColumn.PopupTitleFRA}
                    closable={true}
                    handleModalClose={handlePopupCancel}
                    customClass="custom-popup"
                >
                    <div className="popup-content">
                        <p>{isEng
                            ? htmlToReactParser.parse(popupColumn.PopupMessage || '')
                            : htmlToReactParser.parse(popupColumn.PopupMessageFRA || '')}</p>
                        <div className="popup-buttons">
                            <button
                                className="ontario-button ontario-button--primary"
                                onClick={handlePopupConfirm}
                            >
                                {isEng ? (popupColumn.PopupSaveButtonText || 'Confirm') : (popupColumn.PopupSaveButtonTextFRA || 'Confirmer')}
                            </button>
                            <button
                                className="ontario-button ontario-button--secondary"
                                onClick={handlePopupCancel}
                            >
                                {isEng ? (popupColumn.PopupCancelButtonText || 'Cancel') : (popupColumn.PopupCancelButtonTextFRA || 'Annuler')}
                            </button>
                        </div>
                    </div>
                </CustomPopup>
            )}
        </>
    );
};

export default PagedSummaryListComponent;
